import React, { useState } from "react";
import { ChevronRight, Loader, X } from "lucide-react";
// import FieldOfWork from "components/Reusable/FieldOfWork";
import { FaCheckSquare } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "utils/constant";
import { useAlert } from "../../../components/UI_components/Alert";
import FieldOfWork from "components/Reusable/FieldOfWork";

const UnifiedForm = ({ type, onClose, asSidebar = false }) => {
  const alert = useAlert();
  const [showFieldOfWork, setShowFieldOfWork] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    fieldOfWork: "",
    workname_id: null,
    other_workname: null,
    address: "",
    paymentType: type === "contractor" ? "Contract" : "Daily wage",
    amount: "",
  });

  // ── Phone state: raw 10-digit input + real-time error ──
  const [contactNumber, setContactNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const isContractor = type === "contractor";
  const title = isContractor ? "New Contractor" : "New Worker";
  const nameLabel = isContractor ? "Leader's Name" : "Worker's Name";
  const contactLabel = isContractor
    ? "Leader's Contact number"
    : "Worker's Contact number";

  // ── Strips non-digits, caps at 10, validates length in real time ──
  const handlePhoneInput = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    e.target.value = onlyNums;
    setContactNumber(onlyNums);
    if (onlyNums.length > 0 && onlyNums.length < 10) {
      setPhoneError("Valid 10-digit contact number is required");
    } else {
      setPhoneError("");
    }
    // Clear the step-level contact error too (set by validateStep1 on submit)
    if (errors.contact) {
      setErrors((prev) => ({ ...prev, contact: undefined }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.workname_id && !formData.other_workname) {
      newErrors.fieldOfWork = "Field of work is required";
    }
    // Phone is validated separately via phoneError / contactNumber state
    // if (contactNumber.length !== 10) {
    //   setPhoneError("Valid 10-digit contact number is required");
    //   newErrors.contact = true; // sentinel to block submit
    // }
    if (contactNumber.length > 0 && contactNumber.length !== 10) {
      setPhoneError("Valid 10-digit contact number is required");
      newErrors.contact = true;
    }
    if (!formData.amount || Number(formData.amount) <= 0) {
      newErrors.amount = "Valid amount is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldSelect = (field) => {
    setFormData((prev) => ({
      ...prev,
      fieldOfWork: field.name,
      workname_id: field.id,
      other_workname: field.other_workname ?? null,
    }));
    if (errors.fieldOfWork) {
      setErrors((prev) => ({ ...prev, fieldOfWork: undefined }));
    }
    setShowFieldOfWork(false);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: undefined }));
    }
  };

  const createWorker = async () => {
    try {
      const token = localStorage.getItem("access");
      const site = localStorage.getItem("site");
      const response = await axios.post(
        `${BASE_URL}/worker/create-worker/`,
        {
          types: isContractor ? "Crew" : "Worker",
          name: formData.name,
          contact: contactNumber ? "+91" + contactNumber : null,
          address: formData.address,
          sites: parseInt(site),
          workname_id: formData.workname_id,
          other_workname: formData.other_workname,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.status) {
        return response.data.data.id;
      } else {
        alert.addAlert(response.data.message, "error");
      }
      return null;
    } catch (error) {
      console.error("Failed to create worker:", error);
      setErrors({ submit: "Failed to create worker. Please try again." });
      return null;
    }
  };

  const createPayment = async (createdWorkerId) => {
    try {
      const token = localStorage.getItem("access");
      const response = await axios.post(
        `${BASE_URL}/worker/create-workerpayment/`,
        {
          payment_type: formData.paymentType,
          amount: parseFloat(formData.amount),
          worker_id: createdWorkerId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data.status;
    } catch (error) {
      setErrors({
        submit: "Failed to create payment details. Please try again.",
      });
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaveClicked) return;
    setIsSaveClicked(true);
    if (!validateForm()) {
      setIsSaveClicked(false);
      return;
    }

    setIsLoading(true);
    const createdWorkerId = await createWorker();
    if (!createdWorkerId) {
      setIsLoading(false);
      setIsSaveClicked(false);
      return;
    }

    const paymentSuccess = await createPayment(createdWorkerId);
    setIsLoading(false);
    if (paymentSuccess) {
      alert.addAlert("Worker created successfully", "success");
      onClose();
    } else {
      setIsSaveClicked(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-[black] bg-opacity-30 z-50 ${
        asSidebar ? "flex items-stretch justify-end" : "flex items-center justify-center"
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-[#FAFBFD] shadow-xl relative overflow-hidden ${
          asSidebar
            ? "w-full sm:max-w-xl h-full rounded-l-2xl flex flex-col"
            : "w-full max-w-2xl mx-auto rounded-2xl"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pt-2 flex-1 min-h-0 flex flex-col">
          {/* Dynamic Header */}
          <div className="w-full bg-white border-b sticky top-0 z-10">
            <div className="flex items-center justify-between px-4 py-3">
              {/* <button
                onClick={view === "form" ? onClose : () => setView("form")}
                className="text-gray-600 hover:text-black"
              >
                {view === "form" ? (
                  <X className="h-5 w-5" />
                ) : (
                  <ArrowLeft className="h-5 w-5" />
                )}
              </button> */}
              <div className="w-8" />
              <h2 className="text-[17px] font-semibold text-[#232323]">
                {title}
              </h2>
              <div className="w-8">
                <button
                  onClick={onClose}
                  className="text-gray-600 hover:text-black"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Body Container */}
          <div className="p-4 pb-24 flex-1 overflow-y-auto">
                {errors.submit && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                    {errors.submit}
                  </div>
                )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <h3 className="text-base font-semibold mb-1 text-[#232323]">
                        Worker Details
                      </h3>
                      <p className="text-[13px] text-gray-600 mb-4">
                        Add name, field of work, contact, address and payment details
                      </p>
                    </div>
                    <div className="space-y-4 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                      <div className="space-y-2">
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {nameLabel}
                        </label>
                        <input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Type"
                          className={`mt-1 block w-full rounded-lg bg-[#F3F4F6] px-3 py-2.5 text-sm ${errors.name ? "border border-red-500" : "border border-transparent focus:border-[#3B0069]"}`}
                        />
                        {errors.name && (
                          <p className="text-red-500 text-sm">{errors.name}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <label
                          htmlFor="fieldOfWork"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Field of Work
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowFieldOfWork(true)}
                          className={`mt-1 w-full rounded-lg px-3 py-2.5 bg-[#F3F4F6] text-sm flex items-center justify-between ${errors.fieldOfWork ? "border border-red-500" : "border border-transparent hover:border-gray-300"}`}
                        >
                          <span className="text-left">
                            {formData.fieldOfWork || "Select Field of Work"}
                          </span>
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        </button>
                        {errors.fieldOfWork && (
                          <p className="text-red-500 text-sm">
                            {errors.fieldOfWork}
                          </p>
                        )}
                      </div>

                      {/* ── Contact with +91 prefix & real-time 10-digit validation ── */}
                      <div className="space-y-2">
                        <label
                          htmlFor="contact"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {contactLabel}
                          (Optional)
                        </label>
                        <div
                          className={`flex rounded-lg overflow-hidden bg-[#F3F4F6] ${
                            phoneError ? "border border-red-500" : ""
                          }`}
                        >
                          <span className="inline-flex font-bold items-center px-3 py-2 text-sm text-gray-900 bg-[#F3F4F6]">
                            +91
                          </span>
                          <input
                            id="contact"
                            type="tel"
                            maxLength={10}
                            value={contactNumber}
                            onInput={handlePhoneInput}
                            placeholder="98700 00000"
                            className="flex-1 block rounded-r-md py-2.5 px-3 bg-[#F3F4F6] outline-none text-sm"
                          />
                        </div>
                        {phoneError && (
                          <p className="text-red-500 text-sm">{phoneError}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Payment Type
                        </label>
                        <button
                          type="button"
                          className="w-full flex justify-between items-center px-4 py-2.5 text-sm bg-[#ECE6F0] text-gray-900 rounded-lg border border-[#3B0069]"
                        >
                          {formData.paymentType}
                          <FaCheckSquare className="text-text-clr" />
                        </button>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Amount
                        </label>
                        <div className="relative">
                          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                            ₹
                          </span>
                          <input
                            type="number"
                            id="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            placeholder="Amount"
                            className={`pl-8 block w-full rounded-lg px-4 py-2.5 text-sm border-gray-300 bg-[#F3F4F6] ${errors.amount ? "border border-red-500" : "border border-transparent focus:border-[#3B0069]"}`}
                          />
                        </div>
                        {errors.amount && (
                          <p className="text-red-500 text-sm">
                            {errors.amount}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label
                          htmlFor="address"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Address (Optional)
                        </label>
                        <input
                          id="address"
                          type="text"
                          value={formData.address}
                          onChange={handleInputChange}
                          placeholder="Type"
                          className="mt-1 block w-full rounded-lg bg-[#F3F4F6] px-3 py-2.5 text-sm"
                        />
                      </div>
                      
                    </div>
                    <div className="sticky bottom-0 -mx-4 mt-2 px-4 py-3 border-t border-gray-200 bg-white flex justify-end">
                      <button
                        type="submit"
                        disabled={isLoading || isSaveClicked}
                        className="w-52 h-10 flex justify-center items-center border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-[#3B0069] hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isLoading ? (
                          <>
                            <Loader className="h-5 w-5 animate-spin" />{" "}
                            Processing...
                          </>
                        ) : (
                          <>
                            Create <ChevronRight className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </form>
          </div>
        </div>

        {/* Field of Work Modal */}
        {showFieldOfWork && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-20 z-[60] flex items-center justify-center">
            <div
              className="bg-[#FAFBFD] shadow-xl rounded-2xl w-full max-w-xl mx-4 max-h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="w-full bg-white border-b sticky top-0 z-10">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="w-8" />
                  <h2 className="text-[15px] font-semibold text-[#232323]">
                    Field of Work
                  </h2>
                  <div className="w-8">
                    <button
                      onClick={() => setShowFieldOfWork(false)}
                      className="text-gray-600 hover:text-black"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-3">
                <FieldOfWork
                  onSelect={handleFieldSelect}
                  onClose={() => setShowFieldOfWork(false)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedForm;
